"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { GoogleAnalytics } from "@next/third-parties/google";
import { CONSENT_CHANGE_EVENT, clearGaCookies, getConsent } from "@/lib/consent";

// Inlined at build time (NEXT_PUBLIC_*). Unset in dev/preview → GA never loads.
const GA_ID = process.env.NEXT_PUBLIC_GA_ID;

// Loads Google Analytics 4 ONLY when ALL of:
//   1. the visitor has granted consent, AND
//   2. NEXT_PUBLIC_GA_ID is configured (prod-only env var), AND
//   3. we are NOT on an authenticated /dashboard page (public pages only).
//
// Mounted once globally in app/layout.tsx. Reacts live to consent changes so
// Accept starts tracking with no refresh and Decline/withdraw stops it. Uses
// usePathname only (never useSearchParams) so it needs no Suspense boundary and
// cannot reintroduce the App Router #310 bug.
const GoogleAnalyticsLoader = () => {
  const pathname = usePathname();
  const [granted, setGranted] = useState(false);

  useEffect(() => {
    setGranted(getConsent() === "granted");

    const onChange = () => {
      const isGranted = getConsent() === "granted";
      setGranted(isGranted);
      if (GA_ID) {
        // gtag's official runtime opt-out flag; mirrors consent state.
        (window as unknown as Record<string, boolean>)[`ga-disable-${GA_ID}`] =
          !isGranted;
        if (!isGranted) clearGaCookies();
      }
    };

    window.addEventListener(CONSENT_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_CHANGE_EVENT, onChange);
  }, []);

  if (!GA_ID) return null;
  if (!granted) return null;
  if (pathname?.startsWith("/dashboard")) return null;

  return <GoogleAnalytics gaId={GA_ID} />;
};

export default GoogleAnalyticsLoader;
